import React, { Component } from "react";
import PropTypes from "prop-types";
import Link from "gatsby-link";
import styled from "styled-components";
import { RichText } from "prismic-dom";
import { Box, Flex } from "rebass";
import theme from "../theme.js";
import {
  Head,
  H1,
  H2,
  H3,
  H4,
  Text,
  UpdateCard,
  CardLayout,
  Select,
  LazyImage,
  Article,
  Main,
  PageHeader,
  PageSection,
  PageSidebar,
  PageContent,
  Accordion,
  TinyTile,
  UpdateMeta,
  ResourceCard,
  GradientCard,
} from "components";
import moment from "moment";

const translatedLabels = {
  "en-us": {
    Active: "Active",
    Approved: "Approved",
    Canceled: "Canceled",
    Archived: "Archived",
    Proposed: "Proposed",
  },
  "es-mx": {
    Active: "Activo",
    Approved: "Aprobado",
    Canceled: "Cancelado",
    Archived: "Archivado",
    Proposed: "Propuesto",
  },
  "ar-eg": {
    Active: "جاري",
    Approved: "وافق",
    Canceled: "ألغيت",
    Archived: "أرشفة",
    Proposed: "مقترح",
  },
};

const determineColor = function (region) {
  let color = "violet";
  if (
    region === "update" ||
    region === "institution" ||
    region === null ||
    region === undefined
  ) {
    color = "ash";
  }
  if (
    region === "middle-east-and-north-africa" ||
    region === "oriente-medio-y-norte-de-africa" ||
    region === "الشرق-الاوسط-وشمات-افريقيا"
  ) {
    color = "orange";
  }
  if (region === "africa" || region === "africa1" || region === "افريقيا") {
    color = "green";
  }
  if (
    region === "latin-america" ||
    region === "america-latina-y-el-caribe" ||
    region === "امريكا-اللاتينية"
  ) {
    color = "blue";
  }
  if (region === "asia" || region === "asia1" || region === "اسيا") {
    color = "pink";
  }
  if (
    region === "europe--central-asia" ||
    region === "europa-y-asia-central" ||
    region === "اوروبا-واسيا-الوسطى"
  ) {
    color = "yellow";
  }
  return color;
};

var linkResolver = function (doc) {
  return "/" + doc.type;
};

class Project extends React.Component {
  static contextTypes = {
    setAltLanguages: PropTypes.func,
  };
  componentDidMount() {
    if (this.props.data.page.alternate_languages) {
      this.context.setAltLanguages(this.props.data.page.alternate_languages);
    } else {
      this.context.setAltLanguages(undefined);
    }
    document.body.style.background =
      theme.colors[determineColor(this.props.data.page.data.region.uid)];
  }
  render() {
    let json = undefined;
    if (this.props.data.page.raw) {
      json = JSON.parse(this.props.data.page.raw);
    }
    return (
      <div
        style={
          this.props.transition ? this.props.transition.style : { opacity: 0 }
        }
      >
        <Head
          source={{
            meta_title: [
              {
                text:
                  json.data.title !== undefined
                    ? json.data.title[0]
                      ? json.data.title[0].text + " | Bank Information Center"
                      : ""
                    : "",
              },
            ],
            meta_description: [
              {
                text:
                  json.data.excerpt !== undefined
                    ? json.data.excerpt[0]
                      ? json.data.excerpt[0].text
                      : ""
                    : "",
              },
            ],
            social_sharing_image: {
              url:
                json.data.header_image !== undefined
                  ? json.data.header_image.url
                  : "",
            },
          }}
        />
        <Main>
          <PageHeader
            title={
              json.data.title !== undefined
                ? json.data.title[0]
                  ? json.data.title[0].text
                  : ""
                : ""
            }
            color={json.data.region.uid}
            image={
              json.data.header_image.url
                ? [json.data.header_image.url, json.data.header_image.half.url]
                : ""
            }
          >
            {json.data.excerpt[0] && (
              <Text children={json.data.excerpt[0].text} />
            )}
            {/* {json.data.header_image && (
                  <LazyImage
                    alt={json.data.header_image.alt}
                    blurSrc={
                      json.data.header_image
                        ? json.data.header_image.thumb.url
                        : ""
                    }
                    fullSrc={[
                      json.data.header_image.half.url,
                      json.data.header_image.quarter.url
                    ]}
                  />
                )} */}
          </PageHeader>
          <PageSection>
            <PageSidebar mobileTop="true">
              <Box mt={[-48, null, null, 0]} mx={[0, -32, -48, 0]}>
                <Box mx={[-24, 0]} px={4} py={5} bg="grey.1">
                  {json.data.location && (
                    <Box mb={3}>
                      <H4
                        children={
                          json.lang === "en-us"
                            ? "Location"
                            : json.lang === "es-mx"
                            ? "Ubicación"
                            : "الموقع"
                        }
                        mb={1}
                      />
                      <Text>{json.data.location}</Text>
                    </Box>
                  )}
                  {json.data.cost && (
                    <Box mb={3}>
                      <H4
                        children={
                          json.lang === "en-us"
                            ? "Total Cost"
                            : json.lang === "es-mx"
                            ? "Costo total"
                            : "التكلفة الإجمالية"
                        }
                        mb={1}
                      />
                      <Text>{json.data.cost}</Text>
                    </Box>
                  )}
                  {json.data.funding_source && (
                    <Box mb={3}>
                      <H4
                        children={
                          json.lang === "en-us"
                            ? "Funding"
                            : json.lang === "es-mx"
                            ? "Financiación"
                            : "التمويل"
                        }
                        mb={1}
                      />
                      <Text>{json.data.funding_source}</Text>
                    </Box>
                  )}
                  {json.data.status && (
                    <Box>
                      <H4
                        children={
                          json.lang === "en-us"
                            ? "Status"
                            : json.lang === "es-mx"
                            ? "Estado"
                            : "الحالة"
                        }
                        mb={1}
                      />
                      <Text>
                        {translatedLabels[json.lang][json.data.status]}
                      </Text>
                    </Box>
                  )}
                </Box>
                {this.props.data.author && (
                  <UpdateMeta
                    updateText={
                      json.lang === "en-us"
                        ? "Last update"
                        : json.lang === "es-mx"
                        ? "Última actualización"
                        : "اخر تحديث"
                    }
                    attributionText={
                      json.lang === "en-us"
                        ? "Monitored by"
                        : json.lang === "es-mx"
                        ? "Supervisado por"
                        : "مراقب من قبل"
                    }
                    staffName={
                      this.props.data.author.data.title[0]
                        ? this.props.data.author.data.title[0].text
                        : ""
                    }
                    staffImage={
                      this.props.data.author.data.header_image.url
                        ? this.props.data.author.data.header_image.quarter.url
                        : ""
                    }
                    secondaryStaffName={
                      this.props.data.secondary_author &&
                      this.props.data.secondary_author.data.title[0]
                        ? this.props.data.secondary_author.data.title[0].text
                        : ""
                    }
                    secondaryStaffImage={
                      this.props.data.secondary_author &&
                      this.props.data.secondary_author.data.header_image.url
                        ? this.props.data.secondary_author.data.header_image
                            .quarter.url
                        : ""
                    }
                    // publishDate={
                    //   json.data.publish_date
                    //     ? moment(json.data.publish_date).format("MMMM Do, YYYY")
                    //     : ""
                    // }
                    // updateDate={
                    //   json.last_publication_date
                    //     ? moment(json.last_publication_date).format(
                    //         "MMMM Do, YYYY"
                    //       )
                    //     : ""
                    // }
                    to={
                      this.props.data.author.lang +
                      "/team/" +
                      this.props.data.author.uid
                    }
                    secondaryTo={
                      this.props.data.secondary_author
                        ? this.props.data.author.lang +
                          "/team/" +
                          this.props.data.secondary_author.uid
                        : ""
                    }
                  />
                )}
                <Box mt={1} mx={[-4, 0]}>
                  {this.props.data.region && (
                    <TinyTile
                      title={this.props.data.region.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.page.data.region.uid
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/region/" +
                        this.props.data.region.uid
                      }
                      type={
                        json.lang === "en-us"
                          ? "Region"
                          : json.lang === "es-mx"
                          ? "Regione"
                          : "منطقة"
                      }
                    />
                  )}
                  {this.props.data.institution && (
                    <TinyTile
                      title={this.props.data.institution.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.institution.data.type
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/institution/" +
                        this.props.data.institution.uid
                      }
                      type={
                        json.lang === "en-us"
                          ? "Institution"
                          : json.lang === "es-mx"
                          ? "Institucione"
                          : "لمؤسسة"
                      }
                    />
                  )}
                  {this.props.data.institution2 && (
                    <TinyTile
                      title={this.props.data.institution2.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.institution2.data.type
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/institution/" +
                        this.props.data.institution2.uid
                      }
                      type={
                        json.lang === "en-us"
                          ? "Institution"
                          : json.lang === "es-mx"
                          ? "Institucione"
                          : "لمؤسسة"
                      }
                    />
                  )}
                  {this.props.data.institution3 && (
                    <TinyTile
                      title={this.props.data.institution3.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.institution3.data.type
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/institution/" +
                        this.props.data.institution3.uid
                      }
                      type={
                        json.lang === "en-us"
                          ? "Institution"
                          : json.lang === "es-mx"
                          ? "Institucione"
                          : "لمؤسسة"
                      }
                    />
                  )}
                  {this.props.data.project && (
                    <TinyTile
                      title={this.props.data.project.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.page.data.region.uid
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/project/" +
                        this.props.data.project.uid
                      }
                      type={
                        json.lang === "en-us"
                          ? "Project"
                          : json.lang === "es-mx"
                          ? "Proyecto"
                          : "مشروع"
                      }
                    />
                  )}
                  {this.props.data.campaign && (
                    <TinyTile
                      title={this.props.data.campaign.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.campaign.data.type
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/campaign/" +
                        this.props.data.campaign.uid
                      }
                      type={
                        json.lang === "en-us"
                          ? "Campaign"
                          : json.lang === "es-mx"
                          ? "Campaña"
                          : "حملة"
                      }
                    />
                  )}
                  {this.props.data.campaign2 && (
                    <TinyTile
                      title={this.props.data.campaign2.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.campaign2.data.type
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/campaign/" +
                        this.props.data.campaign2.uid
                      }
                      type={
                        json.lang === "en-us"
                          ? "Campaign"
                          : json.lang === "es-mx"
                          ? "Campaña"
                          : "حملة"
                      }
                    />
                  )}
                  {this.props.data.campaign3 && (
                    <TinyTile
                      title={this.props.data.campaign3.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.campaign3.data.type
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/campaign/" +
                        this.props.data.campaign3.uid
                      }
                      type={
                        json.lang === "en-us"
                          ? "Campaign"
                          : json.lang === "es-mx"
                          ? "Campaña"
                          : "حملة"
                      }
                    />
                  )}
                  {this.props.data.campaign4 && (
                    <TinyTile
                      title={this.props.data.campaign4.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.campaign4.data.type
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/campaign/" +
                        this.props.data.campaign4.uid
                      }
                      type={
                        json.lang === "en-us"
                          ? "Campaign"
                          : json.lang === "es-mx"
                          ? "Campaña"
                          : "حملة"
                      }
                    />
                  )}
                </Box>
              </Box>
            </PageSidebar>
            <PageContent>
              {json.data.body4[0] && (
                <Accordion
                  title={
                    json.lang === "en-us"
                      ? "Overview"
                      : json.lang === "es-mx"
                      ? "Resumen"
                      : "نظرة عامة"
                  }
                  id="overview_tab"
                  data={json.data.body4}
                  startOpen="true"
                />
              )}
              {json.data.body3[0] && (
                <Accordion
                  title={
                    json.lang === "en-us"
                      ? "Concerns"
                      : json.lang === "es-mx"
                      ? "Puntos claves"
                      : "اهتمامات"
                  }
                  id="concerns_tab"
                  data={json.data.body3}
                />
              )}
              {json.data.body2[0] && (
                <Accordion
                  title={
                    json.lang === "en-us"
                      ? "Financing"
                      : json.lang === "es-mx"
                      ? "Financiación"
                      : "تمويل"
                  }
                  id="financing_tab"
                  data={json.data.body2}
                />
              )}
              {json.data.tab_title_1[0] && (
                <Accordion
                  title={json.data.tab_title_1[0].text}
                  id={
                    json.data.tab_title_1[0].text
                      .toLowerCase()
                      .replace(/ /g, "_") + "_tab"
                  }
                  data={json.data.body1}
                />
              )}
              {json.data.tab_title_2[0] && (
                <Accordion
                  title={json.data.tab_title_2[0].text}
                  id={
                    json.data.tab_title_2[0].text
                      .toLowerCase()
                      .replace(/ /g, "_") + "_tab"
                  }
                  data={json.data.body}
                />
              )}
              {json.data.tab_title_3[0] && (
                <Accordion
                  title={json.data.tab_title_3[0].text}
                  id={
                    json.data.tab_title_3[0].text
                      .toLowerCase()
                      .replace(/ /g, "_") + "_tab"
                  }
                  data={json.data.body_3}
                />
              )}
              {json.data.tab_title_4[0] && (
                <Accordion
                  title={json.data.tab_title_4[0].text}
                  id={
                    json.data.tab_title_4[0].text
                      .toLowerCase()
                      .replace(/ /g, "_") + "_tab"
                  }
                  data={json.data.body_4}
                />
              )}
            </PageContent>
          </PageSection>
          {this.props.data.projectResources && (
            <PageSection column="true" borderTop="true">
              <H2
                children={
                  json.lang === "en-us"
                    ? "Related Resources"
                    : json.lang === "es-mx"
                    ? "Recursos Relacionados"
                    : "موارد ذات الصلة"
                }
                mb={5}
                fontSize={[4, 5]}
              />
              <CardLayout w={1 / 1}>
                {this.props.data.projectResources.edges.map(
                  (resource, index) => (
                    <ResourceCard
                      key={"resource" + index}
                      object={resource.node}
                    />
                  )
                )}
              </CardLayout>
            </PageSection>
          )}
          {this.props.data.projectUpdates && (
            <PageSection column="true" borderTop="true">
              <H2
                children={
                  json.lang === "en-us"
                    ? "Related Updates"
                    : json.lang === "es-mx"
                    ? "Actualizaciones relacionadas"
                    : "التحديثات ذات الصلة"
                }
                mb={5}
                fontSize={[4, 5]}
              />
              <CardLayout w={1 / 1}>
                {this.props.data.projectUpdates.edges
                  .sort(function (a, b) {
                    if (
                      moment(a.node.data.publish_date).utc() <
                      moment(b.node.data.publish_date).utc()
                    )
                      return 1;
                    if (
                      moment(a.node.data.publish_date).utc() >
                      moment(b.node.data.publish_date).utc()
                    )
                      return -1;
                    return 0;
                  })
                  .slice(0, 4)
                  .map((update, index) => (
                    <UpdateCard key={"project" + index} object={update.node} />
                  ))}
              </CardLayout>
            </PageSection>
          )}
        </Main>
      </div>
    );
  }
}

export default Project;

export const query = graphql`
  query ProjectQuery(
    $id: String!
    $author: String!
    $secondary_author: String!
    $region: String!
    $institution: String!
    $institution2: String!
    $institution3: String!
    $campaign: String!
    $campaign2: String!
    $campaign3: String!
    $campaign4: String!
  ) {
    page: prismicDocument(prismicId: { eq: $id }) {
      raw
      lang
      uid
      id
      data {
        region {
          uid
        }
      }
      alternate_languages {
        uid
        lang
        type
      }
    }
    author: prismicDocument(prismicId: { eq: $author }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        description {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    secondary_author: prismicDocument(prismicId: { eq: $secondary_author }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        description {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    region: prismicDocument(prismicId: { eq: $region }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    campaign: prismicDocument(prismicId: { eq: $campaign }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    campaign2: prismicDocument(prismicId: { eq: $campaign2 }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    campaign3: prismicDocument(prismicId: { eq: $campaign3 }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    campaign4: prismicDocument(prismicId: { eq: $campaign4 }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    institution: prismicDocument(prismicId: { eq: $institution }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    institution2: prismicDocument(prismicId: { eq: $institution2 }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    institution3: prismicDocument(prismicId: { eq: $institution3 }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    projectUpdates: allPrismicDocument(
      filter: { type: { eq: "update" }, data: { project: { id: { eq: $id } } } }
    ) {
      edges {
        node {
          uid
          type
          lang
          last_publication_date
          data {
            publish_date
            region {
              uid
            }
            title {
              text
            }
            description {
              text
            }
            header_image {
              url
              quarter {
                url
              }
              half {
                url
              }
              thumb {
                url
              }
            }
          }
        }
      }
    }
    projectResources: allPrismicDocument(
      filter: {
        type: { eq: "resource" }
        data: { project: { id: { eq: $id } } }
      }
    ) {
      edges {
        node {
          uid
          type
          lang
          last_publication_date
          data {
            region {
              uid
            }
            title {
              text
            }
            resource_description {
              text
            }
            post_type
            resource_link {
              url
            }
          }
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/layouts/project.js